import { ClientOnly } from "@tanstack/react-router";
import { createPortal } from "react-dom";

import { Button } from "@/web/components/primitives/button";
import { cn } from "@/web/lib/styling";
import { FacadeIllustration } from "@/web/routes/-home/FacadeIllustration";
import type { TransitionStoryRefs } from "@/web/routes/-home/useTransitionStory";

// `max-[760px]:` variants below mirror STORY_MOBILE_MAX_PX in useTransitionStory.

type StoryCopy = { eyebrow: string; title: string; body: string[] };

const STORY_COPY: StoryCopy[] = [
  {
    eyebrow: "01 / A BUILDING",
    title: "THE PLACES THAT MAKE A NEIGHBORHOOD",
    body: [
      "We partner with property owners to transition the buildings a neighborhood depends on into shared ownership: homes for local businesses and places to gather, learn, and create.",
    ],
  },
  {
    eyebrow: "02 / A BUSINESS",
    title: "THE STEWARDS WHO MAKE PLACES VALUABLE",
    body: [
      "Business owners can earn a financial stake in the buildings they help make valuable. As stewards, they care for the place, help shape its future, and share in the value they create.",
    ],
  },
  {
    eyebrow: "03 / THE COMMUNITY",
    title: "THE PEOPLE WITH SKIN IN THE GAME",
    body: [
      "Neighbors and local investors can become shareholders in the building for as little as $100. Each offering recasts the rights, rewards, and responsibilities of ownership.",
    ],
  },
];

const STAGES = ["A building", "A business", "The community"];

/**
 * The sticky scroll story: facade photo → traced line art → an illustrative
 * offering card ("LOCAL GOODS"), with stage copy on the right. All motion is
 * driven by useTransitionStory; the markup here carries the p=0 state.
 *
 * The sticky viewport starts below the fixed MarketingNavbar (`--nav-h`) so
 * the stage bar and the card never sit under the nav's links.
 */
export function TransitionStory({
  refs,
  photoUrl,
  onJump,
}: {
  refs: TransitionStoryRefs;
  photoUrl: string;
  onJump: (stage: number) => void;
}) {
  return (
    <section
      ref={refs.story}
      id="transition"
      data-nav-theme="white"
      aria-label="From Main Street to shared ownership"
      className="transition-story relative h-[500vh]"
    >
      <div
        ref={refs.sticky}
        className="transition-sticky sticky top-(--nav-h) isolate h-[calc(100svh-var(--nav-h))] overflow-hidden bg-white"
      >
        <StoryCard refs={refs} photoUrl={photoUrl} />

        <div
          ref={refs.copy}
          className={cn(
            // Bottom sits just above the progress bar (the prototype reserved the
            // photo caption's space, but the caption has faded out by the time any
            // copy shows) so short screens get the most room before scrolling.
            "story-copy absolute top-[26%] right-[5%] bottom-[12px] left-[64%] opacity-0",
            "max-[760px]:top-[54%] max-[760px]:right-(--gutter) max-[760px]:left-(--gutter)",
          )}
        >
          {STORY_COPY.map((copy, i) => (
            <div
              key={copy.eyebrow}
              ref={(el) => {
                refs.copies.current[i] = el;
              }}
              aria-hidden="true"
              inert
              className="hybrid-copy absolute inset-0 text-[#050f23]"
            >
              <span className="mb-[26px] block text-[12px] leading-[1.5] tracking-[0.12em] text-[#2f2f8c] max-[760px]:mb-[13px] max-[760px]:text-[10px] max-[760px]:tracking-[0.1em]">
                {copy.eyebrow}
              </span>
              <h2
                className={cn(
                  "mb-[30px] font-heading text-[clamp(24px,3vw,49px)] leading-[1.1] font-black tracking-[-0.025em] uppercase",
                  "max-[760px]:mb-[14px] max-[760px]:text-[clamp(23px,5.4vw,33px)] max-[760px]:tracking-[-0.035em]",
                )}
              >
                {copy.title}
              </h2>
              {copy.body.map((text) => (
                <p
                  key={text}
                  className={cn(
                    "mb-[20px] max-w-[450px] text-[clamp(16px,1.4vw,20px)] leading-[1.5] max-[760px]:mb-[10px] max-[760px]:text-[15px] max-[760px]:leading-[1.4]",
                    i === 2 && "max-[760px]:text-[14px]",
                  )}
                >
                  {text}
                </p>
              ))}
            </div>
          ))}
        </div>

        <div
          ref={refs.caption}
          className={cn(
            "photo-caption absolute right-(--gutter) bottom-[35px] left-(--gutter) flex justify-between text-[12px] leading-[normal] tracking-[0.05em] text-white",
            "max-[760px]:bottom-[25px] max-[760px]:gap-[30px] max-[760px]:text-[10px] max-[760px]:[&>span]:max-w-[50%]",
          )}
        >
          <span>A MAIN STREET / A SHARED FUTURE</span>
          <span>A PLACE WORTH KEEPING ↓</span>
        </div>

        <nav
          ref={refs.stages}
          aria-label="Story stages"
          className="hybrid-stages pointer-events-none absolute top-0 right-0 left-0 z-10 grid grid-cols-3 bg-white opacity-0"
        >
          {STAGES.map((label, i) => (
            <Button
              key={label}
              ref={(el) => {
                refs.tabs.current[i] = el;
              }}
              variant="ghost"
              tabIndex={-1}
              onClick={() => onJump(i)}
              className={cn(
                "w-full min-w-0 rounded-none px-[20px] py-[24px] text-center text-[13px] leading-[normal] font-normal text-[#59606d] [--button-border-width:0px]",
                "shadow-[inset_0_-1px_0_#d4d7de] aria-[current=step]:font-bold aria-[current=step]:text-[#2f2f8c] aria-[current=step]:shadow-[inset_0_-2px_0_#2f2f8c]",
                "max-[760px]:px-[8px] max-[760px]:py-[20px] max-[760px]:text-[11px] [&>span:first-child]:block",
              )}
            >
              0{i + 1} <span className="max-[760px]:hidden">{label}</span>
            </Button>
          ))}
        </nav>

        <div className="story-progress absolute right-0 bottom-0 left-0 h-[3px] bg-[#eee]">
          <span
            ref={refs.progress}
            className="block h-full origin-left [transform:scaleX(0)] bg-[#2f2f8c]"
          />
        </div>
      </div>

      <ClientOnly>
        <HandoffLayer layerRef={refs.layer} />
      </ClientOnly>
    </section>
  );
}

/**
 * The fixed layer the story card flies through on its way to the rail. Owned
 * by React (portal) so navigating away from `/` removes it with the page.
 * Carries `home-v2` so the scoped home.css rules still reach the clone.
 */
function HandoffLayer({ layerRef }: { layerRef: TransitionStoryRefs["layer"] }) {
  return createPortal(
    <div ref={layerRef} className="home-v2 offering-handoff" hidden aria-hidden="true" />,
    document.body,
  );
}

/**
 * The illustrative offering card the drawing assembles into. Its content is a
 * story prop, not live data (LOCAL GOODS / 123 Main Street / $100/share / 1,599
 * backers / 77%), but its geometry is the real fundraising browse card's
 * (`UnifiedBrowseCard`), scaled to the frame — see `.story-card` in home.css —
 * so the handoff lands every element on its counterpart in the first rail card.
 * Only `$100/share` has no counterpart; it fades out with the rest of the prop.
 * The frame must stay the canvas's first child: the handoff clone looks it up
 * positionally.
 */
function StoryCard({ refs, photoUrl }: { refs: TransitionStoryRefs; photoUrl: string }) {
  return (
    <div
      ref={refs.canvas}
      className="facade-canvas pointer-events-none absolute top-0 left-0 h-full w-full will-change-[width,height]"
    >
      <div
        ref={refs.frame}
        aria-hidden="true"
        className="build-card-frame story-card pointer-events-none absolute inset-y-0 right-[9%] left-[9%] text-[#2f2f8c] opacity-0 shadow-[inset_0_0_0_1px_#2f2f8c55] max-[760px]:right-[15%] max-[760px]:left-[15%]"
      >
        <div className="story-card-body">
          <div ref={refs.buildBusiness} className="opacity-0">
            <div ref={refs.buildTags} className="story-card-badges">
              <span className="story-card-badge">
                <i className="story-card-dot" />
                FUNDRAISING
              </span>
              <span className="story-card-badge">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="story-card-tag-icon">
                  <path d="M3 3h8l10 10-8 8L3 11Z" />
                  <circle cx="7.5" cy="7.5" r="1" />
                </svg>
                SPECIALTY MARKET
              </span>
            </div>
            <strong ref={refs.buildTitle} className="story-card-title">
              LOCAL GOODS
            </strong>
            <p ref={refs.buildAddress} className="story-card-address">
              123 Main Street
            </p>
          </div>
        </div>
        <div className="story-card-footer">
          <div ref={refs.buildShares} className="story-card-shares opacity-0">
            <strong>$100</strong>
            <span>/share</span>
          </div>
          <div ref={refs.buildBackers} className="opacity-0">
            <div className="story-card-bar">
              <span className="w-[77%]" />
            </div>
            <p className="story-card-stats">
              <span>
                <b>77%</b> funded
              </span>
              <span aria-hidden="true">·</span>
              <span>
                <b>1,599</b> backers
              </span>
            </p>
          </div>
        </div>
      </div>
      <FacadeIllustration
        photoUrl={photoUrl}
        className="facade-svg absolute top-0 block h-full w-full overflow-hidden"
        refs={{
          svg: refs.svg,
          photo: refs.photo,
          ink: refs.ink,
          persistent: refs.persistent,
          neighbors: refs.neighbors,
          business: refs.business,
          shared: refs.shared,
          community: refs.community,
        }}
      />
    </div>
  );
}
